/**
 * HighlightAI · Admin Beta Access
 * -------------------------------------------------
 * Grant / revoke a 6-month Creator plan (beta) without touching Stripe.
 * Admin-only. Fetches from `/api/admin/users` (paginated + search).
 * Every action is confirmed then posted to /beta/grant or /beta/revoke.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Crown, ShieldCheck, ShieldOff,
  ChevronLeft, ChevronRight, Loader2, User as UserIcon,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const PER_PAGE = 25;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
};

const daysLeft = (iso) => {
  if (!iso) return null;
  try {
    const diff = Math.floor((new Date(iso).getTime() - Date.now()) / 86400000);
    return diff;
  } catch { return null; }
};

const PlanBadge = ({ plan, planId, isBeta }) => {
  if (plan === 'pro' && isBeta) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#D4FF00]/10 text-[#D4FF00] border border-[#D4FF00]/30"
        data-testid="plan-badge-beta"
      >
        <Crown className="w-3 h-3" strokeWidth={2.4} />
        Beta · Creator
      </span>
    );
  }
  if (plan === 'pro') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-white/[0.05] text-zinc-200 border border-white/10">
        <Crown className="w-3 h-3" />
        {planId ? planId : 'Pro'}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border border-white/10 text-zinc-500">
      Free
    </span>
  );
};

const BetaStatus = ({ u }) => {
  if (!u.beta_active) {
    return <span className="text-xs text-zinc-600">Non</span>;
  }
  const d = daysLeft(u.beta_expires_at);
  const cls = d !== null && d <= 14
    ? 'text-amber-400'
    : 'text-[#D4FF00]';
  return (
    <div className="flex flex-col gap-0.5">
      <span className={`text-xs font-medium ${cls}`}>Oui</span>
      <span className="text-[10px] text-zinc-500 font-mono">
        {d !== null && d >= 0 ? `${d}j restants` : 'expiré'}
      </span>
    </div>
  );
};

const UserRow = ({ u, onGrant, onRevoke, busyId }) => {
  const busy = busyId === u.id;
  return (
    <tr
      className="border-b border-white/5 hover:bg-white/[0.02] transition-colors"
      data-testid={`beta-user-row-${u.id}`}
    >
      <td className="p-4 min-w-[180px]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center text-zinc-400">
            <UserIcon className="w-3.5 h-3.5" />
          </div>
          <div>
            <div className="text-sm font-medium text-zinc-100 truncate">{u.name}</div>
            {u.role === 'admin' && (
              <div className="text-[10px] uppercase tracking-widest text-[#D4FF00]/80 font-mono">Admin</div>
            )}
          </div>
        </div>
      </td>
      <td className="p-4 text-sm text-zinc-400 font-mono">{u.email}</td>
      <td className="p-4 text-xs text-zinc-500 font-mono whitespace-nowrap">{fmtDate(u.created_at)}</td>
      <td className="p-4">
        <PlanBadge plan={u.plan} planId={u.plan_id} isBeta={u.beta_active} />
      </td>
      <td className="p-4">
        <BetaStatus u={u} />
      </td>
      <td className="p-4 text-xs whitespace-nowrap">
        <div className="text-zinc-300 font-mono">{u.monthly_credits ?? 0}</div>
        {(u.purchased_credits ?? 0) > 0 && (
          <div className="text-[10px] text-[#D4FF00] font-mono">+ {u.purchased_credits}</div>
        )}
      </td>
      <td className="p-4 text-right whitespace-nowrap">
        {u.beta_active ? (
          <Button
            size="sm"
            variant="outline"
            className="border-white/10 bg-transparent hover:bg-white/[0.04] text-zinc-300 text-xs h-8"
            disabled={busy}
            onClick={() => onRevoke(u)}
            data-testid={`beta-revoke-${u.id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><ShieldOff className="w-3.5 h-3.5 mr-1.5" />Retirer l&apos;accès Creator</>)}
          </Button>
        ) : (
          <Button
            size="sm"
            className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold text-xs h-8"
            disabled={busy || u.role === 'admin'}
            onClick={() => onGrant(u)}
            data-testid={`beta-grant-${u.id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (<><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Accorder un accès Creator (6 mois)</>)}
          </Button>
        )}
      </td>
    </tr>
  );
};

// ─────────────────────────────────────────────────────────────────── page

const BetaAccess = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [betaActiveCount, setBetaActiveCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [debounced]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users', {
        params: { search: debounced, page, per_page: PER_PAGE },
      });
      setRows(r.data?.users || []);
      setTotal(r.data?.total || 0);
      setBetaActiveCount(r.data?.beta_active_count || 0);
    } catch (e) {
      toast.error('Impossible de charger la liste des utilisateurs');
    } finally {
      setLoading(false);
    }
  }, [debounced, page]);

  useEffect(() => { if (user?.role === 'admin') load(); }, [user, load]);

  const handleGrant = async (u) => {
    if (!window.confirm(`Accorder un accès Creator (6 mois) à ${u.email} ?`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/beta/grant`);
      toast.success(`Accès Creator accordé à ${u.email} pour 6 mois`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de l\'activation');
    } finally { setBusyId(null); }
  };

  const handleRevoke = async (u) => {
    if (!window.confirm(`Retirer l'accès Creator à ${u.email} ? Il repassera immédiatement en Free.`)) return;
    setBusyId(u.id);
    try {
      await api.post(`/admin/users/${u.id}/beta/revoke`);
      toast.success(`Accès Creator retiré à ${u.email}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la révocation');
    } finally { setBusyId(null); }
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PER_PAGE)), [total]);

  if (user && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="beta-access-page">
      {/* ── Header ── */}
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Retour Admin
      </Link>

      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] text-[#D4FF00] font-mono">Accès bêta</span>
          <h1 className="font-heading text-4xl lg:text-5xl font-black mt-2 tracking-tighter">
            Bêta privée · Creator 6 mois
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl">
            Accorde ou retire un accès Creator gratuit pendant 6 mois à tes bêta-testeurs.
            Système indépendant de Stripe — les accès expirent automatiquement à la fin de la période.
          </p>
        </div>
        <div className="flex gap-3">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 min-w-[130px]" data-testid="beta-stat-active">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Bêta actifs</div>
            <div className="font-heading text-3xl font-black text-[#D4FF00] mt-1">{betaActiveCount}</div>
          </div>
          <div className="rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 min-w-[130px]" data-testid="beta-stat-total">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Total users</div>
            <div className="font-heading text-3xl font-black mt-1">{total}</div>
          </div>
        </div>
      </div>

      {/* ── Search ── */}
      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Chercher par email ou nom…"
          className="pl-10 bg-white/[0.03] border-white/10 h-10"
          data-testid="beta-search-input"
        />
      </div>

      {/* ── Table ── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="rounded-2xl border border-white/5 bg-white/[0.02] overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="beta-users-table">
            <thead className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/5 bg-white/[0.02]">
              <tr>
                <th className="p-4 font-mono">Nom</th>
                <th className="p-4 font-mono">Email</th>
                <th className="p-4 font-mono">Inscription</th>
                <th className="p-4 font-mono">Plan actuel</th>
                <th className="p-4 font-mono">Statut bêta</th>
                <th className="p-4 font-mono">Crédits</th>
                <th className="p-4 font-mono text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-10 text-center text-zinc-500">
                  Aucun utilisateur ne correspond à cette recherche.
                </td></tr>
              ) : (
                rows.map((u) => (
                  <UserRow key={u.id} u={u} onGrant={handleGrant} onRevoke={handleRevoke} busyId={busyId} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* ── Pagination ── */}
      <div className="flex items-center justify-between mt-4 text-xs text-zinc-500">
        <span data-testid="beta-pagination-info">
          Page {page} / {totalPages} · {total} utilisateur{total > 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm" variant="outline"
            className="border-white/10 bg-transparent h-8"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="beta-page-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
          </Button>
          <Button
            size="sm" variant="outline"
            className="border-white/10 bg-transparent h-8"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="beta-page-next"
          >
            <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BetaAccess;
